import _ from "lodash";
import React, { useState } from "react";
import { Modal, Button, ToggleSwitch, Badge } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { connect } from "react-redux";

import { compose, graphql } from "lib/apolloClient";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { FormTextInput } from "UIComponents/InputTextField";
import withLoader from "UIComponents/GqlWithLoader";
import { useI18n } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import { getOrganizationCurriculumProgramsTypesQuery } from "AddOnManager/modules/AddOnManagerQueries";

import {
  GLOBAL,
  MESSAGING,
  MESSAGING_MODULES,
  MESSAGING_MODULES_DETAILS,
  CURRICULUM_PROGRAM,
} from "../../modules/OrgAddOnContants";
import {
  updateAddOnStatus,
  getQueryVariablesForMessaging,
} from "../../modules/OrgAddOnHelpers";

import classes from "./ManageMessaging.scss";
import StaffList from "./StaffList";

const getSections = ({ isCurriculumSplitEnabled, showOverviewAI }) => [
  {
    key: 1,
    label: "common:messaging",
    modules: isCurriculumSplitEnabled
      ? [
          { id: MESSAGING_MODULES.PAID_MODULE, isCurriculumWise: true },
          {
            id: MESSAGING_MODULES.CHATS_AND_CHANNELS,
            isCurriculumWise: true,
          },
          { id: MESSAGING_MODULES.OVERVIEW },
        ]
      : [
          {
            id: MESSAGING_MODULES.CHATS_AND_CHANNELS,
          },
          {
            id: MESSAGING_MODULES.PAID_MODULE,
          },
          { id: MESSAGING_MODULES.OVERVIEW },
        ],
  },
  {
    key: 2,
    label: "common:messaging_with_ai",
    modules: [
      { id: MESSAGING_MODULES.CHAT_CHANNELS_AI_ADD_ONS },
      ...(showOverviewAI ? [{ id: MESSAGING_MODULES.OVERVIEW_WITH_AI }] : []),
      { id: MESSAGING_MODULES.ANALYTICS },
    ],
  },
  {
    key: 3,
    label: "common:messaging_dash_settings",
    modules: [{ id: MESSAGING_MODULES.SETTINGS }],
  },
];

const ModuleSettings = ({
  orgId,
  moduleId,
  setFullScreenLoader,
  data,
  setSelectStaffModalState,
  isMessagingRnpEnabled,
  onSuccessAddOnConfigChange,
  disabled,
}) => {
  const { t } = useI18n(["common"]);
  const {
    label,
    isGlobalToggleAvailable,
    globalToggleLabel,
    isCustomSelectorAvailable,
    customSelectorLabel,
  } = MESSAGING_MODULES_DETAILS[moduleId];
  const dispatch = useDispatch();
  const { status: isEnabledGlobally, enabledUsers } = data;

  const handleGlobalToggle = async () => {
    setFullScreenLoader(true);
    await dispatch(
      updateAddOnStatus({
        orgId,
        moduleId,
        updateOn: GLOBAL,
        addOnType: MESSAGING,
        newStatus: !isEnabledGlobally,
        queryVariables: getQueryVariablesForMessaging(orgId),
      })
    );
    if (!_.isNull(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
    setFullScreenLoader(false);
  };

  const badgeVariant = isEnabledGlobally ? "subtle" : "notifications";

  const openStaffListModal = () => {
    if (isEnabledGlobally) return;
    setSelectStaffModalState(moduleId);
  };

  return (
    <div className={classes.moduleContainer}>
      <div className={classNames("text-body-1", classes.moduleLabel)}>
        {t(label)}
      </div>
      <div className={classes.rightContainer}>
        {isGlobalToggleAvailable && (
          <div className={classes.toggleContainer}>
            <div
              className={classNames(
                "text-body-1",
                "text-textSubtle",
                classes.toggleLabel
              )}
            >
              {t(globalToggleLabel)}
            </div>
            <ToggleSwitch
              onChange={handleGlobalToggle}
              checked={isEnabledGlobally}
              disabled={disabled}
            />
          </div>
        )}
        {isCustomSelectorAvailable && !isMessagingRnpEnabled && (
          <>
            <div
              className={classNames(
                "text-label-3",
                "text-textSubtle",
                classes.toggleLabel
              )}
            >
              {t("common:or_lowercase")}
            </div>
            <Badge
              variant={badgeVariant}
              value={_.size(enabledUsers)}
              placement="topRight"
              disabled={isEnabledGlobally || disabled}
            >
              <Button
                disabled={isEnabledGlobally || disabled}
                variant="outlined-subtle"
                onClick={openStaffListModal}
              >
                {t(customSelectorLabel)}
              </Button>
            </Badge>
          </>
        )}
      </div>
    </div>
  );
};

const CurriculumWiseModuleSettings = ({
  orgId,
  moduleId,
  data,
  setFullScreenLoader,
  curriculumPrograms,
  onSuccessAddOnConfigChange,
  disabled,
}) => {
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();

  const { label } = MESSAGING_MODULES_DETAILS[moduleId] || {};

  return (
    <div className={classes.curriculumModuleContainer}>
      <div className="text-label-2">{t(label)}</div>
      <div className={classes.curriculumProgramsContainer}>
        {_.map(curriculumPrograms, ({ id, label, acronym }) => {
          const isEnabled = _.includes(data.enabledCurriculum, id);

          const handleToggle = async () => {
            setFullScreenLoader(true);
            await dispatch(
              updateAddOnStatus({
                orgId,
                moduleId,
                curriculumId: id,
                updateOn: CURRICULUM_PROGRAM,
                addOnType: MESSAGING,
                newStatus: !isEnabled,
                isSingleCurriculumProgramId: true,
                queryVariables: getQueryVariablesForMessaging(orgId),
              })
            );
            if (!_.isNull(onSuccessAddOnConfigChange))
              onSuccessAddOnConfigChange();
            setFullScreenLoader(false);
          };

          return (
            <div key={id} className={classes.curriculumProgramContainer}>
              <div className={classNames("text-body-1", classes.moduleLabel)}>
                {label}
              </div>

              <div className={classes.toggleContainer}>
                <div
                  className={classNames(
                    "text-body-1",
                    "text-textSubtle",
                    classes.toggleLabel
                  )}
                >
                  {`${acronym} (${id})`}
                </div>
                <ToggleSwitch
                  onChange={handleToggle}
                  checked={isEnabled}
                  disabled={disabled}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Content = ({
  orgId,
  setFullScreenLoader,
  data,
  curriculumPrograms,
  isCurriculumSplitEnabled,
  isMessagingRnpEnabled,
  showOverviewAI,
  onSuccessAddOnConfigChange,
}) => {
  const { t } = useI18n(["common"]);

  const [selectStaffModalState, setSelectStaffModalState] = useState(null);

  const onCloseStaffListModal = () => {
    setSelectStaffModalState(null);
  };

  const sections = getSections({ isCurriculumSplitEnabled, showOverviewAI });
  const isChatsAndChannelsEnabled = _.get(
    data,
    `${MESSAGING_MODULES.CHATS_AND_CHANNELS}.status`,
    true
  );

  return (
    <>
      <div className={classes.contentContainer}>
        <FormTextInput
          size="large"
          label={t("common:org_id")}
          error=""
          value={orgId}
          disabled // It will always be disabled
        />
        {_.map(sections, ({ key, label, modules }) => (
          <section key={key} className={classes.sectionContainer}>
            <div className={classNames("text-label-1", classes.sectionLabel)}>
              {t(label)}
            </div>
            {_.map(modules, ({ id, isCurriculumWise }) => {
              const isRootModule = id === MESSAGING_MODULES.CHATS_AND_CHANNELS;
              const moduleDisabled =
                !isRootModule && !isChatsAndChannelsEnabled;
              return isCurriculumWise ? (
                <CurriculumWiseModuleSettings
                  orgId={orgId}
                  key={id}
                  moduleId={id}
                  curriculumPrograms={curriculumPrograms}
                  data={data[id]}
                  setFullScreenLoader={setFullScreenLoader}
                  onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
                  disabled={moduleDisabled}
                />
              ) : (
                <ModuleSettings
                  orgId={orgId}
                  key={id}
                  moduleId={id}
                  data={data[id]}
                  setFullScreenLoader={setFullScreenLoader}
                  setSelectStaffModalState={setSelectStaffModalState}
                  isMessagingRnpEnabled={isMessagingRnpEnabled}
                  onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
                  disabled={moduleDisabled}
                />
              );
            })}
          </section>
        ))}
      </div>
      {selectStaffModalState ? (
        <StaffList
          orgId={orgId}
          moduleId={selectStaffModalState}
          setFullScreenLoader={setFullScreenLoader}
          onModalClose={onCloseStaffListModal}
          enabledUserIds={data[selectStaffModalState].enabledUsers}
        />
      ) : null}
    </>
  );
};

const modules = [
  MESSAGING_MODULES.PAID_MODULE,
  MESSAGING_MODULES.CHATS_AND_CHANNELS,
  MESSAGING_MODULES.OVERVIEW,
  MESSAGING_MODULES.CHAT_CHANNELS_AI_ADD_ONS,
  MESSAGING_MODULES.OVERVIEW_WITH_AI,
  MESSAGING_MODULES.ANALYTICS,
  MESSAGING_MODULES.SETTINGS,
];

const STABLE_EMPTY_OBJ = {};

const getPermissionsData = _.memoize(data => {
  if (_.isEmpty(data)) return STABLE_EMPTY_OBJ;
  const { enabledUsers, enabledCurriculum } = data;
  return _.reduce(
    modules,
    (acc, moduleId) => {
      acc[moduleId] = {
        status: data[moduleId],
        enabledUsers: enabledUsers[moduleId] || [],
        enabledCurriculum: enabledCurriculum[moduleId] || [],
      };
      return acc;
    },
    {}
  );
});

const mapStateToProps = () => ({
  isData: true,
  isLoading: false,
});

const ContentWithData = compose(
  connect(mapStateToProps),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    options: ({ orgId }) => ({
      fetchPolicy: "cache-and-network",
      variables: getQueryVariablesForMessaging(orgId),
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const { networkStatus } = getInternalToolGenericData;
      const queryData = _.get(
        getInternalToolGenericData,
        "platform.internalToolsGenericQuery",
        STABLE_EMPTY_OBJ
      );
      const isCurriculumSplitEnabled = _.get(
        queryData,
        "data.CURRICULUM_SPLIT",
        false
      );

      const isMessagingRnpEnabled = _.get(queryData, "data.RNP_ENABLED", false);
      const showOverviewAI = _.get(queryData, "data.SHOW_OVERVIEW_AI", false);

      return {
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        data: getPermissionsData(_.get(queryData, "data", STABLE_EMPTY_OBJ)),
        isCurriculumSplitEnabled,
        isMessagingRnpEnabled,
        showOverviewAI,
      };
    },
  }),
  graphql(getOrganizationCurriculumProgramsTypesQuery, {
    name: "getOrganizationCurriculumProgramsTypes",
    options: ({ orgId }) => ({
      variables: {
        organizationId: orgId,
      },
    }),
    skip: ({ isCurriculumSplitEnabled }) => !isCurriculumSplitEnabled,
    props: ({
      getOrganizationCurriculumProgramsTypes,
      ownProps: { isData, isLoading },
    }) => {
      const { networkStatus, node } =
        getOrganizationCurriculumProgramsTypes || {};

      const curriculumPrograms = _.get(node, "curriculumPrograms", []);

      return {
        curriculumPrograms,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(Content);

const ManageMessaging = ({
  orgId,
  closeModal,
  onSuccessAddOnConfigChange = null,
}) => {
  const { t } = useI18n(["common"]);

  const [fullScreenLoader, setFullScreenLoader] = useState(false);

  return (
    <>
      <Modal width="800px" height="700px" isOpen onClose={closeModal}>
        <Modal.Header
          title={t("common:enable_disable_toddle_messaging_features")}
          isClosable
        />
        <Modal.Body isScrollable>
          <ContentWithData
            orgId={orgId}
            setFullScreenLoader={setFullScreenLoader}
            onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footer}>
            <Button
              variant="neutral"
              type="outlined"
              _legacy_variant="outlined-subtle"
              size="large"
              onClick={closeModal}
            >
              {t("common:close")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {fullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

export default ManageMessaging;
